import { computed, unref, Ref } from '@nuxtjs/composition-api';

type MaybeArray<T> = T[] | { items?: T[] };
type Classification = { description?: string };

type Category = { categoryName?: string };
type Collection = { collectionCode?: string };

const useCoveoAdvancedQuery = ({
  categoryMapping,
  collectionMapping,
  classification_occasion,
  classification_material,
  classification_size,
  classification_character,
}: {
  categoryMapping?: MaybeArray<Category> | Ref<MaybeArray<Category>>,
  collectionMapping?: MaybeArray<Collection> | Ref<MaybeArray<Collection>>,
  classification_occasion?: MaybeArray<Classification> | Ref<MaybeArray<Classification>>,
  classification_material?: MaybeArray<Classification> | Ref<MaybeArray<Classification>>,
  classification_size?: MaybeArray<Classification> | Ref<MaybeArray<Classification>>,
  classification_character?: MaybeArray<Classification> | Ref<MaybeArray<Classification>>,
}) => {
  // Helper to normalize input to array of strings
  function extract(mapping: any, key: string) {
    if (!mapping) return [];
    if (Array.isArray(mapping)) return mapping.map((x) => x?.[key]).filter(Boolean);
    if (Array.isArray(mapping.items)) return mapping.items.map((x) => x?.[key]).filter(Boolean);
    return [];
  }

  function extractClassification(mapping: any) {
    if (!mapping) return [];
    if (Array.isArray(mapping)) return mapping.map((x) => x?.description).filter(Boolean);
    if (Array.isArray(mapping.items)) return mapping.items.map((x) => x?.description).filter(Boolean);
    return [];
  }

  const cats = computed(() => extract(unref(categoryMapping), 'categoryName'));
  const collections = computed(() => extract(unref(collectionMapping), 'collectionCode'));
  const classOccasion = computed(() => extractClassification(unref(classification_occasion)));
  const classMaterial = computed(() => extractClassification(unref(classification_material)));
  const classSize = computed(() => extractClassification(unref(classification_size)));
  const classCharacter = computed(() => extractClassification(unref(classification_character)));

  function buildQuery(field: string, values: string[]) {
    if (!values.length) return '';
    const quoted = values.map((v) => `"${v}"`).join(', ');
    return `@${field} == (${quoted})`;
  }

  const catAdvancedQuery = computed(() => buildQuery('categories', cats.value));
  const collectionsAdvancedQuery = computed(() => buildQuery('collectioncode', collections.value));
  const classOccasionAdvancedQuery = computed(() => buildQuery('classification_campaign', classOccasion.value));
  const classMaterialAdvancedQuery = computed(() => buildQuery('classification_material', classMaterial.value));
  const classSizeAdvancedQuery = computed(() => buildQuery('classification_size', classSize.value));
  const classCharacterAdvancedQuery = computed(() => buildQuery('classification_character', classCharacter.value));

  const advancedQueries = computed(() => [
    classOccasionAdvancedQuery.value,
    classMaterialAdvancedQuery.value,
    classSizeAdvancedQuery.value,
    classCharacterAdvancedQuery.value,
    catAdvancedQuery.value,
    collectionsAdvancedQuery.value,
  ].filter(Boolean));

  const advancedQuery = computed(() => advancedQueries.value.join(' AND '));

  return {
    advancedQuery,
    advancedQueries,
    catAdvancedQuery,
    collectionsAdvancedQuery,
    classOccasionAdvancedQuery,
    classMaterialAdvancedQuery,
    classSizeAdvancedQuery,
    classCharacterAdvancedQuery,
  };
}

export default useCoveoAdvancedQuery;
